<script setup>
import ConnectorIcon from 'dashboard/components-next/agents/ConnectorIcon.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ConnectorsAPI from 'dashboard/api/agents/connectors';
import ConnectorForm from '../../connectors/ConnectorForm.vue';
import MarketplaceOrderVerification from '../../connectors/MarketplaceOrderVerification.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({ data: { type: Object, required: true } });
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();
const type = computed(() => props.data.kind.replace('tool_', ''));
const connectors = ref([]);
const types = ref({});
const busy = ref(false);
const result = ref('');
const formVersion = ref(0);
const selected = computed(
  () =>
    connectors.value.find(c => c.id === Number(props.data.connector_id)) || null
);
const tools = computed(
  () => selected.value?.available_tools || types.value[type.value]?.tools || []
);

onMounted(async () => {
  try {
    const { data } = await ConnectorsAPI.get();
    connectors.value = data.connectors.filter(
      c => c.connector_type === type.value
    );
    types.value = data.connector_types;
  } catch {
    useAlert(t('AI_AGENTS.MESSAGES.UPDATE_ERROR'));
  }
});
const selectConnection = event => {
  emit('update', {
    connector_id: Number(event.target.value) || null,
    enabled_tools: [],
    owner_id: '',
  });
  result.value = '';
};
const saveConnection = async attributes => {
  busy.value = true;
  try {
    const { data } = selected.value
      ? await ConnectorsAPI.update(selected.value.id, attributes)
      : await ConnectorsAPI.create(attributes);
    connectors.value = [
      ...connectors.value.filter(c => c.id !== data.id),
      data,
    ];
    emit('update', { connector_id: data.id });
    formVersion.value += 1; // Clear password/token inputs after successful persistence.
    useAlert(t('CONNECTOR_NODE.SAVED'));
  } catch {
    useAlert(t('AI_AGENTS.MESSAGES.UPDATE_ERROR'));
  } finally {
    busy.value = false;
  }
};
const testConnection = async () => {
  busy.value = true;
  try {
    const { data } = await ConnectorsAPI.testConnection(selected.value.id);
    result.value = data.message || data.error;
  } catch {
    result.value = t('AI_AGENTS.MESSAGES.UPDATE_ERROR');
  } finally {
    busy.value = false;
  }
};
const toggleTool = (key, checked) => {
  const enabled = new Set(props.data.enabled_tools || []);
  if (checked) enabled.add(key);
  else enabled.delete(key);
  const dependencies = {
    sienge: ['sienge_units', 'sienge_unit'],
    tecimob: ['tecimob_search', 'tecimob_property'],
    loja_integrada: ['loja_integrada_products', 'loja_integrada_product'],
  };
  const [search, detail] = dependencies[type.value] || [];
  if (key === detail && !checked) enabled.delete(search);
  if (enabled.has(search)) enabled.add(detail);
  emit('update', { enabled_tools: [...enabled] });
};
</script>

<template>
  <div class="space-y-4 p-4">
    <div class="flex items-center justify-between">
      <ConnectorIcon :type="type" />
      <h3 class="text-sm font-semibold text-n-slate-12">{{ data.label }}</h3>
      <button class="i-lucide-x text-n-slate-10" @click="emit('close')" />
    </div>
    <p class="text-sm text-n-slate-11">{{ t('CONNECTOR_NODE.HELP') }}</p>
    <label class="flex items-center gap-2 text-sm text-n-slate-12">
      <input
        type="checkbox"
        :checked="data.enabled !== false"
        @change="emit('update', { enabled: $event.target.checked })"
      />
      {{ t('AI_AGENTS.FLOW.TOOLS.ENABLED') }}
    </label>
    <select
      :value="data.connector_id || ''"
      :aria-label="t('TECIMOB.CONNECTION')"
      class="w-full rounded-lg border border-n-weak bg-n-solid-1 p-2 text-sm text-n-slate-12"
      @change="selectConnection"
    >
      <option value="">{{ t('AI_AGENTS.CONNECTORS.NEW') }}</option>
      <option
        v-for="connector in connectors"
        :key="connector.id"
        :value="connector.id"
      >
        {{ connector.name }}
      </option>
    </select>
    <ConnectorForm
      :key="`${type}-${data.connector_id || 'new'}-${formVersion}`"
      :connector="selected"
      :connector-types="types"
      :selected-type="type"
      :is-saving="busy"
      @save="saveConnection"
      @cancel="emit('close')"
    />
    <template v-if="selected">
      <Button
        :label="t('AI_AGENTS.CONNECTORS.TEST')"
        :is-loading="busy"
        @click="testConnection"
      />
      <p v-if="result" role="status" class="text-sm text-n-slate-11">
        {{ result }}
      </p>
      <MarketplaceOrderVerification
        v-if="type === 'mercado_livre'"
        :key="selected.id"
        :connector-id="selected.id"
      />
      <h4 class="text-sm font-semibold text-n-slate-12">
        {{ t('AI_AGENTS.CONNECTORS.TOOLS_LABEL') }}
      </h4>
      <label
        v-for="tool in tools"
        :key="tool.key"
        class="flex items-start gap-2 text-sm text-n-slate-12"
      >
        <input
          type="checkbox"
          :checked="(data.enabled_tools || []).includes(tool.key)"
          @change="toggleTool(tool.key, $event.target.checked)"
        />
        <span>
          {{ tool.name }}
          <span class="block text-xs text-n-slate-11">
            {{ tool.description }}
          </span>
        </span>
      </label>
      <label v-if="type === 'tecimob'" class="block text-sm text-n-slate-12">
        {{ t('TECIMOB.OWNER') }}
        <input
          :value="data.owner_id || ''"
          class="mt-1 w-full rounded-lg border border-n-weak bg-n-solid-1 p-2"
          @input="emit('update', { owner_id: $event.target.value })"
        />
      </label>
    </template>
    <Button
      :label="t('AI_AGENTS.FLOW.REMOVE_NODE')"
      color="ruby"
      variant="outline"
      @click="emit('remove')"
    />
  </div>
</template>
